import { useEffect, useMemo } from 'react';
import { useFormik } from 'formik';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { editRestaurant } from '../../../api/restaurant';
import { useCuisinesContext } from '../../../contexts/CuisinesContext';
import { useRestaurantContext } from '../../../contexts/RestaurantContext';

import useRestaurantWizardInitialValues from '../RestaurantWizard/useRestaurantWizardInitialValues';
import { RestaurantWizardFormValues, RestaurantWizardSchema } from '../RestaurantWizard/restaurantWizardSchema';

import { Dropdown, Input, PhoneInput } from '../../../components/common/Form';
import RichTextEditor from '../../../components/common/Form/RichTextEditor/RichTextEditor';
import { handleHttpsInput } from '../../../utils/general';
import { htmlToPlainText, initialRichText } from '../../../utils/richText';

import ProfileSectionPage from '../../../components/ProfileSectionPage/ProfileSectionPage';
import './styles/_location-profile.scss';
import LocationProfileActions from './LocationProfileActions';

const LocationBasicInfoPage = () => {
  const { restaurantID } = useParams<{ restaurantID: string }>();

  const { currentRestaurant, isLoading, updateRestaurant } = useRestaurantContext();
  const { cuisines, loadCuisines } = useCuisinesContext();

  useEffect(() => {
    loadCuisines();
  }, [loadCuisines]);

  const wizardInitialValues = useRestaurantWizardInitialValues(true, currentRestaurant);
  const initialValues = useMemo(
    () => ({
      ...wizardInitialValues,
      description: initialRichText(currentRestaurant?.descriptionHtml, currentRestaurant?.description)
    }),
    [wizardInitialValues, currentRestaurant?.descriptionHtml, currentRestaurant?.description]
  );

  const formik = useFormik<RestaurantWizardFormValues>({
    initialValues,
    validationSchema: RestaurantWizardSchema.pick(['name', 'description', 'phone', 'email', 'cuisine', 'website']),
    enableReinitialize: true,
    validateOnBlur: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const phoneNumber = values.phone?.startsWith('+1') ? values.phone.slice(2) : values.phone;

        const description = htmlToPlainText(values.description);

        await editRestaurant({
          name: values.name,
          description,
          descriptionHtml: values.description,
          phone: phoneNumber,
          email: values.email,
          website: values.website
        });

        if (currentRestaurant) {
          updateRestaurant({
            ...currentRestaurant,
            name: values.name,
            description,
            descriptionHtml: values.description,
            phone: phoneNumber,
            email: values.email,
            website: values.website
          });
        }

        toast.success('Basic information saved successfully.');
      } catch {
        toast.error('Failed to save basic information.');
      } finally {
        setSubmitting(false);
      }
    }
  });

  const handleSave = async () => {
    const errors = await formik.validateForm();

    formik.setTouched({
      name: true,
      phone: true,
      email: true
    });

    if (Object.keys(errors).length > 0) {
      return;
    }

    await formik.submitForm();
  };

  if (isLoading || !currentRestaurant?.restaurantID) {
    return null;
  }

  if (restaurantID && Number(restaurantID) !== currentRestaurant.restaurantID) {
    return null;
  }

  return (
    <ProfileSectionPage title="BASIC INFORMATION">
      <form className="location-profile-content" onSubmit={formik.handleSubmit}>
        <div className="location-basic-info-page">
          <Input
            name="name"
            label="RESTAURANT NAME"
            className="location-basic-info-name-input"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.name}
            touched={formik.touched.name}
            error={formik.errors.name}
          />

          <RichTextEditor
            name="description"
            label={
              <span>
                DESCRIPTION <i>(Optional)</i>
              </span>
            }
            className="location-basic-info-description-input"
            onChange={(html) => formik.setFieldValue('description', html)}
            onBlur={() => formik.setFieldTouched('description')}
            value={formik.values.description}
            touched={formik.touched.description}
            error={formik.errors.description}
          />

          <PhoneInput
            name="phone"
            label="PHONE NUMBER"
            className="location-basic-info-phone-input"
            onBlur={formik.handleBlur}
            touched={formik.touched.phone}
            onChange={(phoneNumber: string) => formik.setFieldValue('phone', phoneNumber)}
            value={formik.values.phone}
            error={formik.errors?.phone}
          />

          <Input
            name="email"
            label="EMAIL ADDRESS"
            type="email"
            className="location-basic-info-email-input"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.email}
            touched={formik.touched.email}
            error={formik.errors.email}
          />

          {cuisines?.length > 0 && (
            <Dropdown
              label="CUISINE"
              className="location-basic-info-cuisine-select"
              onChange={(value: string) => formik.setFieldValue('cuisine', parseInt(value, 10))}
              value={formik.values.cuisine}
              values={cuisines.map((cuisine) => ({
                label: cuisine.name,
                value: cuisine.cuisineID
              }))}
            />
          )}

          <Input
            name="website"
            label={
              <span>
                WEBSITE <i>(Optional)</i>
              </span>
            }
            className="location-basic-info-website-input"
            onChange={(event: any) => formik.setFieldValue('website', handleHttpsInput(event.target.value))}
            onBlur={formik.handleBlur}
            value={formik.values.website}
            touched={formik.touched.website}
            error={formik.errors.website}
          />

          <LocationProfileActions onSave={handleSave} />
        </div>
      </form>
    </ProfileSectionPage>
  );
};

export default LocationBasicInfoPage;
